import React, { useState, useCallback, useEffect } from 'react';
import './ReturnsSection.scss';
import { API } from '../API/api';
import locationService from '../helper/location-service';
import Warning from '../Warning/Warning';

// Subcomponents
import ProgressIndicator from './components/ProgressIndicator';
import ContactForm from './components/ContactForm';
import VehicleForm from './components/VehicleForm';
import DisposalForm from './components/DisposalForm';
import ValuationForm from './components/ValuationForm';
import SuccessMessage from './components/SuccessMessage';
import OTPModal from './components/OTPModal';

// Hooks
import { useFormData } from './hooks/useFormData';
import { useVehicleData } from './hooks/useVehicleData';
import { useDisposalData } from './hooks/useDisposalData';
import { useOTP } from './hooks/useOTP';
import { useAnalytics } from './hooks/useAnalytics';

// Constants
import { PROGRESS_STEPS } from './constants';

const ReturnsSection = (props) => {
  // Props destructuring
  const {
    Banner,
    EmailIcon,
    VehicleMaker: vehicleMakers = [],
    VehicleModels: vehicleModelsData = [],
    source,
    sourceId,
  } = props?.returnSectionData || {};
  const titleLines = props?.returnSectionData?.titleLines || [];
  const bannerSrc = Banner?.value?.src;
  const mailSrc = EmailIcon?.value?.src;
  const scourceValue = source?.value;
  const sourceIdValue = sourceId?.value;

  // Custom Hooks
  const { formData, updateFormData } = useFormData();
  const [isValidPincodeSelected, setIsValidPincodeSelected] = useState(false);
  const { vehicleData, updateVehicleData, filteredModels, filteredCities, filteredVehicleMakers } =
    useVehicleData(vehicleMakers, vehicleModelsData);
  const { disposalData, updateDisposalData } = useDisposalData();
  const {
    otp,
    timer,
    otpError,
    isModalOpen,
    otpInputRefs,
    setOtp,
    setTimer,
    setOtpError,
    setIsModalOpen,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    resetOTP,
  } = useOTP();
  const { pushToDataLayer } = useAnalytics();

  // UI State
  const [currentStep, setCurrentStep] = useState(1);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningType, setWarningType] = useState('');
  const [locationSuggestionState, setLocationSuggestionState] = useState(false);

  // Data State
  const [pincodeSuggestionList, setPincodeSuggestionList] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [dealerName, SetDealerName] = useState('');
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [leadId, setLeadID] = useState('');
  const [pincode, setPincode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vehicleCategories = props?.returnSectionData?.VehicleCategory || [];
  const { fetchLocationSuggestions, fetchDealerInformation, getLocationDetailsUsingGeo } =
    locationService(vehicleCategories);

  // API Functions
  const generateOTP = async (mobile) => {
    try {
      const payload = { mobileNo: mobile };
      const response = await fetch(API.submit_details.generate_otp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('OTP generation failed');
      return response.json();
    } catch (error) {
      console.error('Generate OTP failed:', error);
      throw error;
    }
  };

  const verifyOTP = async (otpValue) => {
    try {
      const payload = {
        CustomerName: formData.fullName.trim(),
        Email: formData.email,
        MobileNumber: formData.mobileNumber,
        Source: scourceValue,
        sourceId: sourceIdValue,
        OtpNumber: otpValue,
      };
      const response = await fetch(API.submit_details.verify_otp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('OTP verification failed');
      return response.json();
    } catch (error) {
      console.error('Verify OTP failed:', error);
      throw error;
    }
  };

  const submitForm = async (payload) => {
    try {
      const response = await fetch(API.submit_details.submit_form, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Form submission failed');
      return response.json();
    } catch (error) {
      console.error('Submit form failed:', error);
      throw error;
    }
  };

  // Step Navigation
  const handleStepBack = useCallback(() => {
    const sectionMap = {
      2: 'Vehicle Details',
      3: 'Disposal Details',
      4: 'Validation Details',
    };

    if (sectionMap[currentStep]) {
      const payload = {
        event: 'Enquiry_Form',
        eventCategory: 'Enquiry form',
        eventAction: 'Back',
        State: vehicleData.state || 'NA',
        City: vehicleData.city || 'NA',
        Pincode: pincode || 'NA',
        vehicle_type: vehicleData.vehicleType || 'NA',
        eventLabel: 'NA',
        FormType: 'ELV Form',
        section_name: sectionMap[currentStep],
      };

      if (currentStep !== 2) {
        payload.DealerName = dealerName || 'NA';
      }

      pushToDataLayer(payload);
    }

    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, [currentStep, pushToDataLayer, pincode, dealerName, vehicleData]);

  // Contact Form Submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    pushToDataLayer({
      event: 'Enquiry_Form',
      eventCategory: 'Enquiry form',
      eventAction: 'Send OTP',
      eventLabel: 'NA',
      FormType: 'ELV Form',
      section_name: 'Contact Details',
    });

    try {
      const response = await generateOTP(formData.mobileNumber);
      if (response?.Result === 'success' || response?.Success) {
        setIsModalOpen(true);
      } else {
        alert(response.Message);
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // OTP Verification
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.some((digit) => digit === '')) return;

    setOtpError('');
    setIsSubmitting(true);

    try {
      const response = await verifyOTP(otp.join(''));

      if (response?.Result === 'success' || response?.Success) {
        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Submit',
          eventLabel: 'Success',
          ErrorMessage: response?.Message || 'NA',
          FormType: 'ELV Form',
          section_name: 'Contact Details',
        });
        setIsModalOpen(false);
        resetOTP();
        setCurrentStep(2);
        setLeadID(response?.LeadId);
      } else {
        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Submit',
          ErrorMessage: response?.Message || 'NA',
          eventLabel: 'Fail',
          FormType: 'ELV Form',
          section_name: 'Contact Details',
        });
        setOtpError(response?.Message || 'Invalid OTP. Please try again.');
        setOtp(Array(otp.length).fill(''));
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      setOtpError('Verification failed. Please try again.');
      setOtp(Array(otp.length).fill(''));
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (timer > 0) return;

    try {
      const response = await generateOTP(formData.mobileNumber);
      if (response?.Result === 'success' || response?.Success) {
        setTimer(299);
        resetOTP();
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      setOtpError('Failed to resend OTP. Please try again.');
    }
  };

  // Vehicle Form Submission
  const handleVehicleSubmit = (e) => {
    e.preventDefault();
    const trimmedPincode = vehicleData.pincode?.trim();

    if (!trimmedPincode) {
      alert('Pincode/Location is required.');
      return;
    }

    if (!isValidPincodeSelected) {
      alert('Please enter the correct Pincode/Location.');
      return;
    }

    pushToDataLayer({
      event: 'Enquiry_Form',
      eventCategory: 'Enquiry form',
      eventAction: 'Next',
      eventLabel: 'NA',
      Pincode: pincode,
      State: vehicleData.state || 'NA',
      City: vehicleData.city || 'NA',
      vehicle_type: vehicleData.vehicleType,
      FormType: 'ELV Form',
      section_name: 'Vehicle Details',
    });

    const isVehicleWithinRestrictedAge = (purchaseDate, category) => {
      if (!purchaseDate) return false;

      const purchase = new Date(purchaseDate);
      const today = new Date();

      const selectedCategory = vehicleCategories.find((cat) => cat.displayName === category);

      const yearsText = selectedCategory?.fields?.Years?.value || '';
      const ageLimit = parseInt(yearsText, 10) || 15;

      const limitDate = new Date(purchase);
      limitDate.setFullYear(limitDate.getFullYear() + ageLimit);

      return today < limitDate;
    };

    if (isVehicleWithinRestrictedAge(vehicleData.yearOfPurchase, vehicleData.vehicleCategory)) {
      setWarningType('ageWarning');
      setIsWarningModalOpen(true);
      return;
    }
    setCurrentStep(3);
  };

  // Location Handling
  const handleLocationSuggestion = async (value) => {
    updateVehicleData({ pincode: value });
    setIsValidPincodeSelected(false);

    if (!value.trim() || value.trim().length < 3) {
      setLocationSuggestionState(false);
      setPincodeSuggestionList([]);
      return;
    }

    try {
      const response = await fetchLocationSuggestions(value);
      setPincodeSuggestionList(response?.data || []);
      setLocationSuggestionState(Boolean(response?.data?.length));
    } catch (error) {
      console.error('Location suggestion failed:', error);
      setLocationSuggestionState(false);
    }
  };

  const handleLocationSelection = async (item) => {
    try {
      const response = await getLocationDetailsUsingGeo(item.geo);
      const { pincode: pincodeValue, state, city } = response || {};

      setPincode(pincodeValue || '');
      updateVehicleData({
        pincode: item.name,
        state: state || '',
        city: city || '',
      });
      setIsValidPincodeSelected(true);

      const dealList = await fetchDealerInformation(pincodeValue, vehicleData.vehicleCategory);
      const dealerStores = dealList?.stores || [];

      setDealers(dealerStores);

      if (!dealerStores.length) {
        setWarningType('noDealer');
        setIsWarningModalOpen(true);
        setPincode('');
        updateVehicleData({
          pincode: '',
          state: '',
          city: '',
        });
        setIsValidPincodeSelected(false);
      }
    } catch (error) {
      console.error('Location selection failed:', error);
    } finally {
      setLocationSuggestionState(false);
    }
  };

  // Disposal Form Submission
  const handleDisposalSubmit = (e) => {
    e.preventDefault();

    pushToDataLayer({
      event: 'Enquiry_Form',
      eventCategory: 'Enquiry form',
      eventAction: 'Next',
      eventLabel: 'NA',
      FormType: 'ELV Form',
      section_name: 'Disposal Details',
    });
    setCurrentStep(4);
  };

  // Dealer Selection
  const handleDealerSelect = useCallback((dealer) => {
    setSelectedDealerId(dealer.dealer_id);
    SetDealerName(dealer?.wp_name);
  }, []);

  // Final Submission
  const handleValuationSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDealerId) {
      alert('Please select a dealer to continue.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        CustomerName: formData.fullName,
        Email: formData.email,
        City: vehicleData.city,
        Pincode: pincode,
        CustomerState: vehicleData.state,
        LeadId: leadId,
        UpdateDate: new Date().toISOString().replace('T', ' ').slice(0, 23),
        EventIds: [2],
        UpdatedBy: 1,
        ExtraAttributes: [
          { AttributeName: 'Mobile', AttributeValue: formData.mobileNumber },
          { AttributeName: 'AgreeToTerms', AttributeValue: String(formData.agreeToTerms) },
          { AttributeName: 'VehicleCategory', AttributeValue: vehicleData.vehicleCategory },
          { AttributeName: 'VehicleType', AttributeValue: vehicleData.vehicleType },
          { AttributeName: 'VehicleMaker', AttributeValue: vehicleData.vehicleMaker },
          { AttributeName: 'VehicleModel', AttributeValue: vehicleData.vehicleModel },
          {
            AttributeName: 'YearOfPurchase',
            AttributeValue: vehicleData.yearOfPurchase ? vehicleData.yearOfPurchase : 'null',
          },
          {
            AttributeName: 'DealerId',
            AttributeValue: selectedDealerId,
          },
          {
            AttributeName: 'OriginalRC',
            AttributeValue: disposalData.originalRc === 'yes' ? 'true' : 'false',
          },
          {
            AttributeName: 'KYCMatch',
            AttributeValue: disposalData.kycMatch === 'yes' ? 'true' : 'false',
          },
          {
            AttributeName: 'Hypothecation',
            AttributeValue: disposalData.hypothecation === 'yes' ? 'true' : 'false',
          },
          {
            AttributeName: 'CriminalRecord',
            AttributeValue: disposalData.criminalRecord === 'yes' ? 'true' : 'false',
          },
          {
            AttributeName: 'PendingLiabilities',
            AttributeValue: disposalData.pendingLiabilities === 'yes' ? 'true' : 'false',
          },
        ],
      };

      const result = await submitForm(payload);

      if (result?.Status === '200' || result?.Success) {
        const selectedDealer = dealers.find((d) => d.dealer_id === selectedDealerId);

        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Submit',
          eventLabel: 'Success',
          Pincode: pincode,
          DealerName: dealerName || 'NA',
          vehicle_type: vehicleData.vehicleType,
          State: vehicleData.state || 'NA',
          City: vehicleData.city || 'NA',
          FormType: 'ELV Form',
          section_name: 'Validation Details',
        });

        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Vehicle Scrap Confirmation',
          eventLabel: 'NA',
          FormType: 'ELV Form',
          section_name: 'Validation Details',
        });

        setCurrentStep(5);
      } else {
        pushToDataLayer({
          event: 'Enquiry_Form',
          eventCategory: 'Enquiry form',
          eventAction: 'Submit',
          eventLabel: 'Fail',
          Pincode: pincode,
          vehicle_type: vehicleData.vehicleType,
          State: vehicleData.state || 'NA',
          City: vehicleData.city || 'NA',
          FormType: 'ELV Form',
          section_name: 'Validation Details',
        });
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Warning Modal Handlers
  const handleWarningConfirm = () => {
    if (warningType === 'ageWarning') {
      pushToDataLayer({
        event: 'Enquiry_Form',
        eventCategory: 'Enquiry form',
        eventAction: 'Yes Proceed',
        eventLabel: 'NA',
        Pincode: pincode,
        vehicle_type: vehicleData.vehicleType,
        State: vehicleData.state || 'NA',
        City: vehicleData.city || 'NA',
        FormType: 'ELV Form',
        section_name: 'Vehicle Details',
      });
      setCurrentStep(3);
    }
    setIsWarningModalOpen(false);
    setWarningType('');
  };

  const handleWarningClose = () => {
    if (warningType === 'ageWarning') {
      // Track "Cancel" action
      pushToDataLayer({
        event: 'Enquiry_Form',
        eventCategory: 'Enquiry form',
        eventAction: 'Cancel',
        eventLabel: 'NA',
        Pincode: pincode,
        vehicle_type: vehicleData.vehicleType,
        State: vehicleData.state || 'NA',
        City: vehicleData.city || 'NA',
        FormType: 'ELV Form',
        section_name: 'Vehicle Details',
      });
    }
    setIsWarningModalOpen(false);
    setWarningType('');
  };

  const selectedCategory = props?.returnSectionData?.VehicleCategory?.find(
    (cat) => cat.displayName === vehicleData.vehicleCategory
  );

  const vehicleYears = selectedCategory?.fields?.Years?.value || '';

  useEffect(() => {
    setPincode('');
    setDealers([]);
    setSelectedDealerId(null);
    setIsValidPincodeSelected(false);
  }, [vehicleData.vehicleCategory]);

  return (
    <section id="returns-form" className="returns-section">
      <div className="container customContainer">
        <div className="returns-content">
          <div className="returns-image-wrapper">
            {bannerSrc && (
              <img
                src={bannerSrc}
                alt="Returns On Your Return - TVS Vehicle Scrapping Program"
                className="returns-image"
              />
            )}
            <div className="form-banner-img">
              <h3>
                {titleLines[0]?.fields?.Name?.value} {titleLines[1]?.fields?.Name?.value}
              </h3>
            </div>
          </div>

          <div className="returns-form-wrapper">
            <div className="form-card">
              <h2 className="form-title">Goodbye Old Ride, Hello Rewards!</h2>

              <ProgressIndicator steps={PROGRESS_STEPS} currentStep={currentStep} />

              {currentStep === 1 && (
                <ContactForm
                  formData={formData}
                  onChange={updateFormData}
                  onSubmit={handleContactSubmit}
                  isSubmitting={isSubmitting}
                  pushToDataLayer={pushToDataLayer}
                  privacyLink={props?.returnSectionData?.Privacy?.value}
                  termsLink={props?.returnSectionData?.Terms?.value}
                />
              )}

              {currentStep === 2 && (
                <VehicleForm
                  vehicleCategories={props?.returnSectionData?.VehicleCategory || []}
                  vehicleData={vehicleData}
                  onChange={updateVehicleData}
                  onSubmit={handleVehicleSubmit}
                  onBack={handleStepBack}
                  filteredVehicleMakers={filteredVehicleMakers}
                  filteredModels={filteredModels}
                  locationSuggestionState={locationSuggestionState}
                  pincodeSuggestionList={pincodeSuggestionList}
                  onLocationSuggestion={handleLocationSuggestion}
                  onLocationSelection={handleLocationSelection}
                  setLocationSuggestionState={setLocationSuggestionState}
                  pushToDataLayer={pushToDataLayer}
                />
              )}

              {currentStep === 3 && (
                <DisposalForm
                  disposalQuestions={props?.returnSectionData?.DisposableQuestions || []}
                  disposalData={disposalData}
                  onChange={updateDisposalData}
                  onSubmit={handleDisposalSubmit}
                  onBack={handleStepBack}
                  pushToDataLayer={pushToDataLayer}
                />
              )}

              {currentStep === 4 && (
                <ValuationForm
                  vehicleCategories={props?.returnSectionData?.VehicleCategory || []}
                  scrapValueText={props?.returnSectionData?.ScrapValueText?.value}
                  scrapMessage={props?.returnSectionData?.ScrapMessage?.value}
                  vehicleTerm={props?.returnSectionData?.VehicleTerms?.value}
                  vehicleData={vehicleData}
                  dealers={dealers}
                  selectedDealerId={selectedDealerId}
                  onDealerSelect={handleDealerSelect}
                  onSubmit={handleValuationSubmit}
                  onBack={handleStepBack}
                  isSubmitting={isSubmitting}
                  mailSrc={mailSrc}
                  pushToDataLayer={pushToDataLayer}
                />
              )}

              {currentStep === 5 && (
                <SuccessMessage
                  vehicleCategory={vehicleData.vehicleCategory}
                  vehicleCategories={props?.returnSectionData?.VehicleCategory || []}
                  scrapValueText={props?.returnSectionData?.ScrapValueText?.value}
                  scrapMessage={props?.returnSectionData?.ScrapMessage?.value}
                  scrapSuccessMessage={props?.returnSectionData?.ScrapSuccessMessage?.value}
                  scrapCongratulations={props?.returnSectionData?.ScrapCongratulations?.value}
                  scrapCompletionIcon={props?.returnSectionData?.ScrapCompletionIcon?.value}
                  vehicleTerm={props?.returnSectionData?.VehicleTerms?.value}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <OTPModal
          otp={otp}
          timer={timer}
          otpError={otpError}
          isSubmitting={isSubmitting}
          otpInputRefs={otpInputRefs}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onOtpPaste={handleOtpPaste}
          onSubmit={handleOtpSubmit}
          onResend={handleResendOTP}
          onClose={() => {
            setIsModalOpen(false);
            resetOTP();
          }}
          pushToDataLayer={pushToDataLayer}
        />
      )}

      <Warning
        isOpen={isWarningModalOpen}
        onClose={handleWarningClose}
        onConfirm={handleWarningConfirm}
        title={warningType === 'noDealer' ? 'Oops!' : 'Hold On!'}
        message={
          warningType === 'noDealer' ? (
            <>No dealer available for selected location.</>
          ) : (
            <>
              The selected vehicle is{' '}
              <span className="warning-modal-highlight">less than {vehicleYears} old</span>. Are you
              sure you want to proceed with scrapping?
            </>
          )
        }
        showActions={warningType !== 'noDealer'}
      />
    </section>
  );
};

export default ReturnsSection;
